import React, { useEffect, useState } from "react";
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import useSWR from "swr";
import { useCapContext } from "../../providers/CapContext";
import { CapNearbyCustomersMap } from "../molecules/CapNearbyCustomersMap";
import {
  fetchNearbyCustomers,
  NearbyCustomersResponse,
} from "../../../services/nearbyCustomers";
import { BrandBlueButton, StyledCapPhotoModal } from "./HomeReadinessStepView";
import { rollbar } from "providers/rollbar";
import { useAppSelector } from "store";

const normalizeProspectCoordinate = (
  value: number | string | null | undefined
): number | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const parsed = typeof value === "string" ? parseFloat(value) : value;

  return Number.isFinite(parsed) ? parsed : undefined;
};

const CapNearbyCustomersSection = () => {
  const { prospect, isCustomer } = useCapContext();
  const proposalToken = useAppSelector((state) => state?.auth?.proposalToken);
  const hybridToken = useAppSelector((state) => state?.auth?.hybridToken);
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const [showMapModal, setShowMapModal] = useState(false);

  const customerCity = prospect?.customerCity;
  const latitude = normalizeProspectCoordinate(prospect?.latitude);
  const longitude = normalizeProspectCoordinate(prospect?.longitude);
  const shouldFetch = latitude !== undefined && longitude !== undefined;

  const {
    data: nearbyCustomers,
    error: nearbyCustomersError,
    isLoading: isNearbyCustomersLoading,
  } = useSWR<NearbyCustomersResponse>(
    shouldFetch
      ? [
          "Get Nearby Customers",
          latitude,
          longitude,
          isCustomer,
          proposalToken,
          hybridToken,
        ]
      : null,
    () =>
      fetchNearbyCustomers(latitude!, longitude!, {
        isCustomer,
        proposalToken,
        hybridToken,
      })
  );

  useEffect(() => {
    if (nearbyCustomersError) {
      rollbar.error(nearbyCustomersError as Error, {
        context: "CapNearbyCustomersSection",
        customerCity,
        isCustomer,
        hasLatitude: latitude !== undefined,
        hasLongitude: longitude !== undefined,
      });
    }
  }, [customerCity, isCustomer, latitude, longitude, nearbyCustomersError]);

  if (isNearbyCustomersLoading) {
    return (
      <SectionContainer isMobile={isMobile} isTablet={isTablet}>
        <LoadingSkeleton />
      </SectionContainer>
    );
  }

  if (
    nearbyCustomersError ||
    !nearbyCustomers?.formattedCount ||
    !customerCity ||
    latitude === undefined ||
    longitude === undefined
  ) {
    if (!customerCity) {
      return null;
    }

    return (
      <SectionContainer isMobile={isMobile} isTablet={isTablet}>
        <CopyContainer>
          <Typography
            size={isMobile ? 20 : 24}
            color={tokens.BRAND_HERO_BLUE}
            lineHeight={isMobile ? 28 : 32}
          >
            More people in {customerCity} are choosing solar than ever before.
          </Typography>
        </CopyContainer>
      </SectionContainer>
    );
  }

  const neighborCopy = `${nearbyCustomers.formattedCount} neighbors near ${customerCity} have gone solar with Sunrun`;

  return (
    <>
      <SectionContainer isMobile={isMobile} isTablet={isTablet}>
        <CopyContainer>
          <Typography
            size={isMobile ? 20 : 24}
            color={tokens.BRAND_HERO_BLUE}
            lineHeight={isMobile ? 28 : 32}
          >
            {neighborCopy}
          </Typography>
          <Disclaimer>
            Showing up to 200 pins for homes within a 25 mile radius that have
            gone solar with Sunrun
          </Disclaimer>
        </CopyContainer>
        <MapThumbnailContainer isMobile={isMobile}>
          <CapNearbyCustomersMap
            latitude={latitude}
            longitude={longitude}
            locations={nearbyCustomers.locations ?? []}
            testId="cap-nearby-customers-map-inline"
          />
          <ExpandMapButton
            type="button"
            aria-label="Open nearby customers map"
            data-testid="cap-nearby-customers-map-expand-button"
            onClick={() => setShowMapModal(true)}
          >
            <SvgIcon
              width={24}
              height={24}
              name={SvgNames.ArrowsOutSimple}
              color={tokens.TINTS_OFF_WHITE_90}
            />
          </ExpandMapButton>
        </MapThumbnailContainer>
      </SectionContainer>

      {showMapModal && (
        <StyledCapPhotoModal
          isTablet={isTablet}
          isMobile={isMobile}
          onClose={() => setShowMapModal(false)}
          testId="cap-nearby-customers-map-modal"
        >
          <ModalContent>
            <Typography
              size={isMobile ? 20 : 24}
              color={tokens.BRAND_HERO_BLUE}
              lineHeight={isMobile ? 28 : 32}
            >
              {neighborCopy}
            </Typography>
            <Disclaimer>
              Showing up to 200 pins for homes within a 25 mile radius that have
              gone solar with Sunrun
            </Disclaimer>
            <ModalMapContainer>
              <CapNearbyCustomersMap
                latitude={latitude}
                longitude={longitude}
                locations={nearbyCustomers.locations ?? []}
                showZoomControls
                testId="cap-nearby-customers-map-modal-map"
              />
            </ModalMapContainer>
          </ModalContent>
        </StyledCapPhotoModal>
      )}
    </>
  );
};

const LoadingSkeleton = styled(Skeleton)<{ isMobile?: boolean }>`
  height: ${({ isMobile }) => (isMobile ? "180px" : "220px")};
  width: 100%;
`;

const SectionContainer = styled.section<{
  isMobile: boolean;
  isTablet: boolean;
}>`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? "column" : "row")};
  align-items: ${({ isMobile }) => (isMobile ? "stretch" : "center")};
  gap: ${({ isMobile }) => (isMobile ? "16px" : "32px")};
  margin-top: 32px;
  padding: ${({ isMobile, isTablet }) =>
    isMobile ? "24px 16px" : isTablet ? "32px 24px" : "32px 64px"};
  background: ${tokens.BACKGROUND_LIGHT};
  border-radius: 8px;
`;

const CopyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: 14px;
  font-style: italic;
  line-height: 16px;
  color: ${tokens.BRAND_HERO_BLUE};
  opacity: 0.7;
`;

const MapThumbnailContainer = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: ${({ isMobile }) => (isMobile ? "100%" : "280px")};
  height: ${({ isMobile }) => (isMobile ? "180px" : "220px")};
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ExpandMapButton = styled(BrandBlueButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  margin: 0;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 70vh;
  min-height: 420px;
`;

const ModalMapContainer = styled.div`
  flex: 1;
  min-height: 320px;
  border-radius: 8px;
  overflow: hidden;
`;

export { CapNearbyCustomersSection };
